/**
 * Cart "source" attribution helpers.
 *
 * When a retailer / sales user lands on the order page through a promo banner,
 * a notification, a story or an advertisement pop-up, the order page is opened with a `masterSearch`
 * query plus one of the ids below. While that master search is active every
 * add-to-cart call carries the ids so the backend knows which marketing
 * platform drove the sale. Once the master search is cleared the ids are
 * dropped (all zeros).
 */

export type ClickPlatform = 'promo' | 'notification' | 'story';

export interface CartSource {
  notificationId: number;
  bannerId: number;
  storyId: number;
  popupId: number;
}

export const EMPTY_CART_SOURCE: CartSource = {
  notificationId: 0,
  bannerId: 0,
  storyId: 0,
  popupId: 0,
};

const CART_SOURCE_KEYS: Array<keyof CartSource> = ['notificationId', 'bannerId', 'storyId', 'popupId'];

const toId = (value: string | number | null | undefined): number => {
  if (value == null || value === '') return 0;
  const n = parseInt(String(value), 10);
  return Number.isFinite(n) && n > 0 ? n : 0;
};

/** Read notificationId / bannerId / storyId / popupId from a location.search string. */
export const parseCartSourceFromSearch = (search: string): CartSource => {
  const params = new URLSearchParams(search);
  return {
    notificationId: toId(params.get('notificationId')),
    // `promoId` is accepted as an alias of `bannerId`
    bannerId: toId(params.get('bannerId')) || toId(params.get('promoId')),
    storyId: toId(params.get('storyId')),
    popupId: toId(params.get('popupId')),
  };
};

export const hasCartSource = (source?: Partial<CartSource> | null): boolean =>
  !!source && CART_SOURCE_KEYS.some((key) => (source[key] || 0) > 0);

/** Map a click platform + reference id to the add-to-cart source fields. */
export const cartSourceForPlatform = (
  platform: ClickPlatform,
  referenceId: number | string | null | undefined,
): CartSource => {
  const id = toId(referenceId);
  return {
    ...EMPTY_CART_SOURCE,
    ...(platform === 'notification' ? { notificationId: id } : {}),
    ...(platform === 'promo' ? { bannerId: id } : {}),
    ...(platform === 'story' ? { storyId: id } : {}),
  };
};

/**
 * Append the non-zero source ids to a relative URL (keeps any query already on it).
 * e.g. appendCartSourceToUrl('/retailer/order?masterSearch=1001', { storyId: 7 })
 *   -> '/retailer/order?masterSearch=1001&storyId=7'
 */
export const appendCartSourceToUrl = (url: string, source: Partial<CartSource>): string => {
  const [pathAndQuery, hash] = url.split('#');
  const [path, query = ''] = pathAndQuery.split('?');
  const params = new URLSearchParams(query);
  CART_SOURCE_KEYS.forEach((key) => {
    const value = source[key] || 0;
    if (value > 0) params.set(key, String(value));
    else params.delete(key);
  });
  const qs = params.toString();
  return `${path}${qs ? `?${qs}` : ''}${hash ? `#${hash}` : ''}`;
};

/** Build the order page URL for a master search coming from a given platform. */
export const buildOrderUrlWithSource = (
  basePath: string,
  masterSearch: string | null | undefined,
  platform: ClickPlatform,
  referenceId: number | string | null | undefined,
): string => {
  const url = masterSearch
    ? `${basePath}?masterSearch=${encodeURIComponent(masterSearch)}`
    : basePath;
  return appendCartSourceToUrl(url, cartSourceForPlatform(platform, referenceId));
};
